import React, { useRef, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { motion, AnimatePresence } from 'framer-motion';
import { Info } from 'lucide-react';
import { Card } from '../utils/cards';

export function SortableCard(props: {
    id: string, card: Card, index: number, handleCardClick: (card: Card, index: number, cardImageRef: any) => void, offeredCards: {
        playerId: string;
        cardId: string;
    }[]
},) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: props.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    let cardImgRef = useRef(null);
    const [showInfoIcon, setShowInfoIcon] = useState<boolean>(false);
    const [showCardTooltip, setShowCardTooltip] = useState<boolean>(false);
    const [isCardBeingHoveredOver, setIsCardBeingHoveredOver] = useState(false);

    const cardVariants = {
        idle: {
            scale: 1,
            y: 0,
            rotateY: 0,
            boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)"
        },
        hover: {
            scale: 1.05,
            y: -12,
            rotateY: 5,
            boxShadow: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
            transition: {
                duration: 0.2,
                ease: "easeOut"
            }
        },
        dragging: {
            scale: 1.1,
            rotate: 5,
            opacity: 0.8,
            transition: {
                duration: 0.2
            }
        }
    };

    const renderInfoToolTipInformation = () => {
        let cardName = "";
        let cardDescription = "";

        if (!props.card) {
            return <span>Invalid card</span>;
        }

        if (props.card.type === "Number") {
            cardName = props.card.name;
        }
        else {
            if (props.card.color !== "White" && props.card.color !== "Black") {
                cardName += props.card.color + " "
            }

            cardName += props.card.name;
        }

        if (props.card.description != null) {
            cardDescription = props.card.description;
            if (props.card.color !== "White" && props.card.color !== "Black") {
                cardDescription += " " + cardName + " can be played on any color of " + props.card.name + " or any other " + props.card.color + " card."
            }
            else {
                cardDescription += " " + cardName + " can be played on any other card on your turn."
            }
        }
        else {
            cardDescription += cardName + " can be played on any color of " + props.card.number + " or any other " + props.card.color + " card."
        }

        return <React.Fragment>
            <h4>{cardName}</h4>
            <p style={{ "fontSize": "14px" }}>{cardDescription}</p>
        </React.Fragment>
    }

    const renderOfferedTooltipInformation = () => {
        let cardName = "";

        if (!props.card) {
            return <span>Invalid card</span>;
        }

        if (props.card.type === "Number") {
            cardName = props.card.name;
        }
        else {
            if (props.card.color !== "White" && props.card.color !== "Black") {
                cardName += props.card.color + " "
            }

            cardName += props.card.name;
        }

        return <React.Fragment>
            <p style={{ "fontSize": "18px", margin: "none" }}>You have offered this {cardName} to the current player</p>
        </React.Fragment>
    }

    const hasCardBeenOffered = () => {
        for (let i = 0; i < props.offeredCards.length; i++) {
            if (props.offeredCards[i].cardId === props.card.id) {
                return true;
            }
        }
        return false
    }

    const onMouseHasEnteredCardContainer = () => {
        setShowInfoIcon(true);
        setIsCardBeingHoveredOver(true);
    }

    const onMouseHasLeftCardContainer = () => {
        setShowInfoIcon(false);
        setIsCardBeingHoveredOver(false);
    }

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners} className='sortable-card-container'>
            <motion.div
                className="card-wrapper"
                variants={cardVariants}
                initial="idle"
                animate={isDragging ? "dragging" : (isCardBeingHoveredOver ? "hover" : "idle")}
                onMouseEnter={onMouseHasEnteredCardContainer}
                onMouseLeave={onMouseHasLeftCardContainer}
                onTap={() => { setShowInfoIcon(false); props.handleCardClick(props.card, props.index, cardImgRef) }}
                style={{
                    perspective: "1000px",
                    cursor: "pointer"
                }}
            >
                <motion.img
                    ref={cardImgRef}
                    key={props.id}
                    className={`player-heated-card-modern ${hasCardBeenOffered() ? "this-card-has-been-offered" : ""}`}
                    src={props.card.image}
                    alt={props.card.name}
                    whileTap={{ scale: 0.95 }}
                />

                <AnimatePresence>
                    {hasCardBeenOffered() && (
                        <motion.div
                            className='offered-badge'
                            initial={{ opacity: 0, scale: 0.8, y: 10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.8, y: 10 }}
                            transition={{ duration: 0.2 }}
                        >
                            <span>OFFERED</span>
                        </motion.div>
                    )}
                </AnimatePresence>

                <AnimatePresence>
                    {showInfoIcon && (
                        <motion.div
                            className='info-button'
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            transition={{ duration: 0.2 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowInfoIcon(false);
                                props.handleCardClick(props.card, props.index, cardImgRef);
                            }}
                            onMouseEnter={() => setShowCardTooltip(true)}
                            onMouseLeave={() => setShowCardTooltip(false)}
                        >
                            <Info size={16} />
                        </motion.div>
                    )}
                </AnimatePresence>

                <AnimatePresence>
                    {showCardTooltip && (
                        <motion.div
                            className='card-tooltip'
                            initial={{ opacity: 0, y: 10, scale: 0.9 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.9 }}
                            transition={{ duration: 0.2 }}
                        >
                            {hasCardBeenOffered() ? renderOfferedTooltipInformation() : renderInfoToolTipInformation()}
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>

            <style>{`
                .sortable-card-container {
                    position: relative;
                    display: inline-block;
                }

                .card-wrapper {
                    position: relative;
                    display: inline-block;
                }

                .player-heated-card-modern {
                    width: 6rem;
                    border-radius: var(--radius-lg);
                    transition: none;
                    border: 2px solid rgba(255, 255, 255, 0.1);
                    box-shadow: var(--shadow-md);
                }

                .player-heated-card-modern.this-card-has-been-offered {
                    border-color: var(--primary-400);
                    box-shadow: 0 0 20px rgba(233, 69, 96, 0.3), var(--shadow-lg);
                }

                .offered-badge {
                    position: absolute;
                    top: -8px;
                    right: -8px;
                    background: linear-gradient(135deg, var(--primary-400), #ff6b8a);
                    color: white;
                    font-size: 0.625rem;
                    font-weight: 700;
                    padding: var(--space-xs) var(--space-sm);
                    border-radius: var(--radius-md);
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    box-shadow: var(--shadow-md);
                    z-index: 10;
                }

                .info-button {
                    position: absolute;
                    top: var(--space-sm);
                    right: var(--space-sm);
                    background: rgba(255, 255, 255, 0.9);
                    backdrop-filter: blur(10px);
                    color: var(--gray-700);
                    width: 2rem;
                    height: 2rem;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    box-shadow: var(--shadow-sm);
                    z-index: 10;
                }

                .info-button:hover {
                    background: rgba(255, 255, 255, 1);
                    transform: scale(1.1);
                }

                .card-tooltip {
                    position: absolute;
                    bottom: 100%;
                    left: -50%;
                    transform: translateX(-50%);
                    background: rgba(0, 0, 0, 0.9);
                    backdrop-filter: blur(15px);
                    color: white;
                    padding: var(--space-md);
                    border-radius: var(--radius-lg);
                    max-width: 400px;
                    width: 200%;
                    z-index: 20;
                    margin-bottom: var(--space-sm);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    box-shadow: var(--shadow-xl);
                }

                .card-tooltip h4 {
                    margin: 0 0 var(--space-sm) 0;
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: var(--primary-400);
                }

                .card-tooltip p {
                    margin: 0;
                    font-size: 0.75rem;
                    line-height: 1.2;
                    color: var(--gray-300);
                }

                @media (max-width: 768px) {
                    .player-heated-card-modern {
                        width: 4rem;
                    }

                    .card-tooltip {
                        max-width: 200px;
                        font-size: 0.6875rem;
                    }
                }
            `}</style>
        </div>
    );
}
